import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';

import {
  TableHeader,
  TableContainer,
  SearchForm,
  IconButton,
  Button,
  SearchFormProps,
  FolderCopyOutlinedIcon,
  AddIcon,
  FilterButton,
  isFiltersSelected,
  Tab,
  Tabs,
} from '@occc/ui-design-system';
import { ContainerColumnLeft, snackbar } from '@occc-app/design-system';
import { LocationFilter } from '@occc/graphql';
import {
  useLocationGroupCreateMutation,
  useAddLocationsToGroupMutation,
} from '@occc-app/dataAccessLayer/locationGroup/mutations';
import { useTabQueryParam } from '@occc/utils-query-params';
import { useFilterParam } from '@occc-app/hooks/useFilterParam';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useBulkSelect } from '@occc-app/routes/Location/useBulkSelect';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { SiteManagementTab } from './types';
import { getIsDeleted, tabLabelsMap } from './utils';
import { LocationListContainer } from './LocationsList/LocationListContainer';
import {
  Location_Status,
  LocationFilterFormValues,
  LocationListContainerProps,
  StatusType,
} from './LocationsList/types';
import {
  LOCATIONS_AFTER_PARAM,
  LOCATIONS_BEFORE_PARAM,
  LOCATIONS_PARAM_NAMES,
  DEFAULT_SITE_MANAGEMENT_TAB,
  SITE_MANAGEMENT_TAB_PARAM,
  SITE_MANAGEMENT_TABS,
} from './constants';

import { SiteManagementFilterForm } from './SiteManagementFilterForm';
import { LocationGroupsTableContainer } from '../../LocationGroup/LocationGroupsTableContainer';
import { LocationGroupFormValues } from '../../LocationGroup/types';
import { LocationGroupCreateDialog } from '../../LocationGroup/LocationGroupCreateDialog';
import AddToLocationGroupDialog from '../../components/AddToLocationGroupDialog';
import { AddToLocationGroupDialogFormValues } from '../../components/AddToLocationGroupDialog/types';

// TODO Component requires refactoring

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const targetingCommonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const leftTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const rightTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

const STATUS_PARAM = 'status';
const SITE_ID_PARAM = 'siteIdSearch';
const ADDRESS_PARAM = 'addressSearch';

const filterPaginationParams = [
  { beforeParam: LOCATIONS_BEFORE_PARAM, afterParam: LOCATIONS_AFTER_PARAM },
];

export const SiteManagement: React.FC = () => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = useMemo(() => {
    return {
      ...bindPopover({
        ...popupState,
      }),
    };
  }, [popupState]);
  const isUserPermittedToCreateLocationGroup = useIsUserPermittedTo(Feature.LOCATION_GROUP_CREATE);
  const isUserPermittedToEditLocationGroup = useIsUserPermittedTo(Feature.LOCATION_GROUP_EDIT);
  const isUserPermittedToEditLocation = useIsUserPermittedTo(Feature.LOCATION_EDIT);

  const [activeTab, setActiveTab] = useTabQueryParam<SiteManagementTab>(
    SITE_MANAGEMENT_TAB_PARAM,
    DEFAULT_SITE_MANAGEMENT_TAB,
    SITE_MANAGEMENT_TABS,
  );

  const [addToLocationGroupDialogOpen, setAddToLocationGroupDialogOpen] = useState<boolean>(false);
  const [createLocationGroupDialogOpen, setCreateLocationGroupDialogOpen] =
    useState<boolean>(false);

  const { isBulkSelected, bulkSelectedItems, handleBulkSelectAllClick, handleBulkSelect } =
    useBulkSelect();

  const handleOpenAddToLocationGroupDialog = () => {
    setAddToLocationGroupDialogOpen(!addToLocationGroupDialogOpen);
  };

  const handleOpenCreateLocationGroupDialog = () => {
    setCreateLocationGroupDialogOpen(!createLocationGroupDialogOpen);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: SiteManagementTab,
  ) => {
    setActiveTab(value);
  };

  const onLocationGroupCreateCompleted = () => {
    snackbar.success('New Location Group was created');
    handleOpenCreateLocationGroupDialog();
  };

  const onLocationGroupWithLocationsCreateCompleted = () => {
    snackbar.success('Locations were added to Location Group.');
    handleBulkSelectAllClick([]);
    handleOpenCreateLocationGroupDialog();
  };

  const onLocationGroupCreateError = () => {
    snackbar.error('Unable to create Location Group. Please try a different name.');
  };

  const onAddLocationsToGroupCompleted = () => {
    snackbar.success('Locations were added to Location Group.');
    handleBulkSelectAllClick([]);
    handleOpenAddToLocationGroupDialog();
  };

  const onAddLocationsToGroupError = () => {
    snackbar.error('Unable to update Location Group.');
  };

  const [createLocationGroup, { loading: locationGroupCreateLoading }] =
    useLocationGroupCreateMutation({
      onCompleted: onLocationGroupCreateCompleted,
      onError: onLocationGroupCreateError,
      refetchQueries: ['locationGroups'],
    });

  const [createLocationGroupWithLocations, { loading: locationGroupWithLocationsCreateLoading }] =
    useLocationGroupCreateMutation({
      onCompleted: onLocationGroupWithLocationsCreateCompleted,
      onError: onLocationGroupCreateError,
      refetchQueries: ['locationGroups'],
    });

  const [addLocationsToGroup, { loading: addLocationsToGroupLoading }] =
    useAddLocationsToGroupMutation({
      onCompleted: onAddLocationsToGroupCompleted,
      onError: onAddLocationsToGroupError,
    });

  const onLocationGroupCreateDialogFormSubmit = React.useCallback(
    async (values: LocationGroupFormValues) => {
      const isBulkLocationAddFlow = bulkSelectedItems.length > 0 && activeTab !== 'groups';

      if (isBulkLocationAddFlow) {
        await createLocationGroupWithLocations({
          variables: {
            input: {
              name: values.name,
              locationUuids: bulkSelectedItems,
            },
          },
        });
        return;
      }

      await createLocationGroup({
        variables: {
          input: {
            name: values.name,
            locationUuids: values.locations?.map((location) => location.uuid),
          },
        },
      });
    },
    [createLocationGroup, createLocationGroupWithLocations, bulkSelectedItems, activeTab],
  );

  const onAddToLocationGroup = React.useCallback(
    async (values: AddToLocationGroupDialogFormValues) => {
      if (values.locationGroup) {
        await addLocationsToGroup({
          variables: {
            input: {
              locationGroupUuid: values.locationGroup.uuid,
              locationUuids: bulkSelectedItems,
            },
          },
        });
      }
    },
    [addLocationsToGroup, bulkSelectedItems],
  );

  const [locationsSearch, setLocationsSearch] = useSearchParams(LOCATIONS_PARAM_NAMES);

  const [status, setStatus] = useFilterParam<StatusType>(STATUS_PARAM, Location_Status.Active, {
    paginationParams: filterPaginationParams,
  });
  const [siteId, setSiteId] = useFilterParam<string>(SITE_ID_PARAM, '', {
    paginationParams: filterPaginationParams,
  });
  const [address, setAddress] = useFilterParam<string>(ADDRESS_PARAM, '', {
    paginationParams: filterPaginationParams,
  });

  const locationsTabFilter: LocationFilter = {
    search: locationsSearch,
    isDeleted: getIsDeleted(status),
    siteSystemConfiguration: siteId ? { identifierContains: siteId } : undefined,
    addressSearch: address || undefined,
  };

  const [locationsPaginationInfo, onLocationsPageChange] = usePaginationParams({
    beforeParamName: LOCATIONS_BEFORE_PARAM,
    afterParamName: LOCATIONS_AFTER_PARAM,
  });

  const locationListProps: Omit<LocationListContainerProps, 'filter'> = {
    isBulkSelected,
    bulkSelectedItems,
    handleBulkSelectAllClick,
    handleBulkSelect,
    isUserPermittedToEdit: isUserPermittedToEditLocation,
    getAdditionalRowMenuOptions: isUserPermittedToEditLocationGroup
      ? (location) => [
          {
            label: 'Add to Group',
            onClick: () => {
              handleBulkSelectAllClick([location.uuid]);
              handleOpenAddToLocationGroupDialog();
            },
          },
        ]
      : undefined,
  };

  const onSiteManagementSubmit = (values: LocationFilterFormValues) => {
    setStatus(values.status);
    setSiteId(values.siteId);
    setAddress(values.address);
    popoverProps.onClose();
  };

  const onSiteManagementReset = React.useCallback(() => {
    setStatus(undefined);
    setSiteId('');
    setAddress('');
    popoverProps.onClose();
  }, [popoverProps, setStatus, setSiteId, setAddress]);

  const onSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    if (activeTab === 'locations') {
      setLocationsSearch(value);
    }
  };

  const initSearch: Record<SiteManagementTab, string | undefined> = {
    locations: locationsSearch,
    groups: undefined,
  };

  const headerActions = (activeTab: SiteManagementTab) => {
    if (activeTab === 'groups' && isUserPermittedToCreateLocationGroup) {
      return (
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleOpenCreateLocationGroupDialog}
        >
          Add Group
        </Button>
      );
    }
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm onSubmit={onSearchSubmit} initialValues={{ search: initSearch[activeTab] }} />
        <FilterButton
          {...bindTrigger(popupState)}
          ref={anchorRef(popupState)}
          showBadge={isFiltersSelected([status, siteId, address], [Location_Status.Active, '', ''])}
        />
      </Stack>
    );
  };

  return (
    <>
      <ContainerColumnLeft>
        <TableContainer>
          <TableHeader
            disableTitleTypography
            tabs={
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab
                  value={'locations' satisfies SiteManagementTab}
                  label={tabLabelsMap.locations}
                />
                <Tab value={'groups' satisfies SiteManagementTab} label={tabLabelsMap.groups} />
              </Tabs>
            }
            actions={headerActions(activeTab)}
            bulkSelected={bulkSelectedItems.length}
            bulkActions={
              <IconButton onClick={handleOpenAddToLocationGroupDialog}>
                <FolderCopyOutlinedIcon />
              </IconButton>
            }
          />
          <Box style={targetingBoxStyles}>
            <Box
              id={'locations' satisfies SiteManagementTab}
              style={leftTabStyles(activeTab === 'locations')}
            >
              <LocationListContainer
                {...locationListProps}
                filter={locationsTabFilter}
                paginationInfo={locationsPaginationInfo}
                onPageChange={onLocationsPageChange}
              />
            </Box>
            <Box
              id={'groups' satisfies SiteManagementTab}
              style={rightTabStyles(activeTab === 'groups')}
            >
              <LocationGroupsTableContainer />
            </Box>
          </Box>
          <Popover
            {...popoverProps}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: {
                  maxWidth: 460,
                  minWidth: 300,
                },
              },
            }}
            keepMounted
          >
            <SiteManagementFilterForm
              onSubmit={onSiteManagementSubmit}
              onReset={onSiteManagementReset}
              initialValues={{ status, siteId, address }}
            />
          </Popover>
        </TableContainer>
      </ContainerColumnLeft>

      {createLocationGroupDialogOpen && (
        <LocationGroupCreateDialog
          open={createLocationGroupDialogOpen}
          onClose={handleOpenCreateLocationGroupDialog}
          onSubmit={onLocationGroupCreateDialogFormSubmit}
          loading={locationGroupCreateLoading || locationGroupWithLocationsCreateLoading}
        />
      )}
      {addToLocationGroupDialogOpen && (
        <AddToLocationGroupDialog
          open={addToLocationGroupDialogOpen}
          onClose={handleOpenAddToLocationGroupDialog}
          onCreateNewGroupClick={handleOpenCreateLocationGroupDialog}
          onSubmit={onAddToLocationGroup}
          loading={addLocationsToGroupLoading}
        />
      )}
    </>
  );
};
